import React, { FC, ReactElement, useEffect, useRef, useState } from 'react';
import { Button, Col, Container, Image, Row } from 'react-bootstrap';
import { ITrainingConsultation, ITrainingVideo } from '../../domain/interfaces/api/ITraining';
import Carousel from '../Carousel/Carousel';
import ContentLoader from 'react-content-loader';
import { ITrainingProps } from '../../domain/interfaces/components/ITrainingProps';
import './Training.css';
import { ServiceTypeEnum } from '../../domain/enums/ServiceTypeEnum';
import TrainingItem from './TrainingItem';
import { IAnalyticsEvents, trackEvent } from '../../helpers/analytics/analyticsHelpers';

const TrainingElement: FC<ITrainingProps> = (props): ReactElement => {

  const [hasViewedItems, setHasViewedItems] = useState(false);
  const sectionRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!sectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasViewedItems) {
            // Dispatch Google Analytics event
            trackEvent(IAnalyticsEvents.ViewItems, {
              event_action: 'ViewItems',
              event_category: props.type,
              non_interaction: true,
              event_title: props.title,
            })

            setHasViewedItems(true); // Ensure it only fires once
          }
        });
      },
      {
        threshold: 0.3, // 30% visible = considered "viewed"
      }
    );

    observer.observe(sectionRef.current);

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, [hasViewedItems, props.elements]);

  const CustomLeftArrow = (props: any) => {
    const { onClick } = props;

    return (
      <div onClick={onClick} className="training-slide-arrow prev-arrow">
        <Image loading="lazy" src="/img/arrow_left.png" />
      </div>
    );
  };

  const CustomRightArrow = (props: any) => {
    const { onClick } = props;

    return (
      <div onClick={onClick} className="training-slide-arrow next-arrow">
        <Image loading="lazy" src="/img/arrow_right.png" />
      </div>
    );
  };

  const carouselSettings = {
    dots: false,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    // swipeToSlide: true,
    prevArrow: <CustomLeftArrow />,
    nextArrow: <CustomRightArrow />,
    className: 'training-slider',
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2
        }
      },
      {
        breakpoint: 767,
        settings: {
          slidesToShow: 1
        }
      }
    ]
  };

  const loader = (
    <ContentLoader
      className="loading"
      speed={2}
      width="100%"
      height={450}
      viewBox="0 0 200 450"
      backgroundColor="#f3f3f3"
      foregroundColor="#ecebeb"
      {...props}
    >
      <rect x="0" y="0" rx="0" ry="0" width="100%" height="210" />
      <rect x="0" y="230" rx="13" ry="13" width="93" height="24" />
      <rect x="0" y="274" rx="0" ry="0" width="100%" height="24" />
      <rect x="0" y="320" rx="0" ry="0" width="95%" height="10" />
      <rect x="0" y="340" rx="0" ry="0" width="85%" height="10" />
      <rect x="0" y="360" rx="0" ry="0" width="75%" height="10" />
      <rect x="0" y="390" rx="0" ry="0" width="100%" height="58" />
    </ContentLoader>
  );

  const renderLoaders = () => {
    return [loader, loader, loader];
  };

  return (
    <>
      <Col
        className={`bg-wrapper ${props.type}`}
        ref={sectionRef}
        style={{
          backgroundSize: 'cover',
          backgroundImage: `url(${props.background})`
        }}
      >
        <Container style={{ margin: '0 auto' }}>
          <h1 className="text-white mb-5 training-title pt-5">{props.title}</h1>

          {props.topbar}

          <Row className="justify-content-center mt-5">
            <Col style={{ position: 'relative' }}>
              <Carousel settings={carouselSettings}>
                {props.elements
                  ? props.elements.map(
                      (
                        item: ITrainingVideo | ITrainingConsultation,
                        index: number
                      ) => (
                        <TrainingItem
                          key={index}
                          element={item}
                          type={props.type}
                        />
                      )
                    )
                  : renderLoaders().map((item: any, index: number) => (
                      <div key={index}>{item}</div>
                    ))}
              </Carousel>
            </Col>
          </Row>
        </Container>
      </Col>

      {props.nrelements && (
        <Col className="text-center mt-5 mb-5">
          <Button
            className={`${
              props.type === ServiceTypeEnum.VIDEO ? 'purple-btn' : 'pink-btn'
            }`}
            size="lg"
            href={`/${props.type}/null/-1/-1`}
          >
            Zobacz wszystkie {props.nrelements}{' '}
            {props.type === ServiceTypeEnum.VIDEO ? 'nagrań' : 'konsultacji'}
          </Button>
        </Col>
      )}
    </>
  );
};

export default TrainingElement;
